import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { notifications } from '@/lib/notifications';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    IconDeviceFloppy, IconCheck, IconArrowLeft, IconX,
    IconLayoutSidebarRightExpand, IconLayoutSidebarRightCollapse,
    IconEye, IconBraces, IconRefresh, IconRubberStamp,
} from '@tabler/icons-react';
import LexicalDocEditor from '../../../components/common/LexicalDocEditor';
import PlaceholderManager from '../../../components/admin/PlaceholderManager';
import templateSetService from '../../../services/templateSetService';

const R2_URL = import.meta.env.VITE_R2_PUBLIC_URL || '';

function isLexicalContentEmpty(json) {
    if (!json) return true;
    try {
        const parsed = typeof json === 'string' ? JSON.parse(json) : json;
        const children = parsed?.root?.children;
        if (!Array.isArray(children) || children.length === 0) return true;
        return children.every(node => !Array.isArray(node.children) || node.children.length === 0);
    } catch { return true; }
}

const formatSavedAgo = (savedAt) => {
    if (!savedAt) return '';
    const sec = Math.max(0, Math.round((Date.now() - savedAt) / 1000));
    if (sec < 3)  return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.round(sec / 60);
    if (min < 60) return `${min}m ago`;
    return `${Math.round(min / 60)}h ago`;
};

/* ─── Small toolbar button helpers ──────────────────────────────────────────── */
function TBtn({ onClick, title, disabled, loading, className = '', children }) {
    return (
        <button
            type="button"
            title={title}
            disabled={disabled || loading}
            onClick={onClick}
            className={`inline-flex items-center justify-center gap-1 px-2 py-1 text-xs rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            {loading
                ? <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" /></svg>
                : children
            }
        </button>
    );
}
function IconTBtn({ onClick, title, active, activeClass = '', inactiveClass = 'text-gray-500 hover:bg-gray-100', children }) {
    return (
        <button
            type="button"
            title={title}
            onClick={onClick}
            className={`p-1.5 rounded transition-colors ${active ? activeClass : inactiveClass}`}
        >
            {children}
        </button>
    );
}

/* ─── Main page ──────────────────────────────────────────────────────────────── */
export default function DocumentEditorPage() {
    const params = useParams();
    const { docId, templateSetId, id, mode } = params;
    const navigate = useNavigate();

    const [loading, setLoading]                     = useState(true);
    const [saving, setSaving]                       = useState(false);
    const [doc, setDoc]                             = useState(null);
    const [initialState, setInitialState]           = useState(null);
    const [error, setError]                         = useState(null);
    const [currentDocId, setCurrentDocId]           = useState(null);
    const [placeholderPanelOpen, setPlaceholderPanelOpen] = useState(true);
    const [previewLoading, setPreviewLoading]       = useState(false);
    const [contentLoadFailed, setContentLoadFailed] = useState(false);
    const [reloadKey, setReloadKey]                 = useState(0);

    const [contentDirty, setContentDirty]       = useState(false);
    const [placeholderDirty, setPlaceholderDirty] = useState(false);
    const [lastSavedAt, setLastSavedAt]         = useState(null);
    const [, setNowTick]                        = useState(0);

    const [showHeader, setShowHeader]       = useState(false);
    const [showFooter, setShowFooter]       = useState(false);
    const [headerContent, setHeaderContent] = useState('');
    const [footerContent, setFooterContent] = useState('');

    const lexicalJsonRef          = useRef(null);
    const placeholderManagerRef   = useRef(null);
    const contentDirtyRef         = useRef(false);
    const placeholderDirtyRef     = useRef(false);
    const showHeaderRef           = useRef(false);
    const showFooterRef           = useRef(false);
    const headerContentRef        = useRef('');
    const footerContentRef        = useRef('');
    const [stamp, setStamp]       = useState(false);
    const stampRef                = useRef(false);
    const [pageSettings, setPageSettings] = useState(null);
    const pageSettingsRef         = useRef(null);
    const initialChangeConsumedRef = useRef(false);

    const isDirty = contentDirty || placeholderDirty;

    /* Load document */
    useEffect(() => {
        const loadDoc = async () => {
            setLoading(true); setError(null); setInitialState(null);
            setContentLoadFailed(false); setDoc(null);
            lexicalJsonRef.current = null;
            initialChangeConsumedRef.current = false;
            contentDirtyRef.current = false; placeholderDirtyRef.current = false;
            setContentDirty(false); setPlaceholderDirty(false); setLastSavedAt(null);

            try {
                let targetDocId = docId;
                if (id && mode) {
                    const templateRes = await templateSetService.getTemplateSetById(id);
                    const templateSet = templateRes.data;
                    if (templateSet.docs && templateSet.docs.length > 0) {
                        targetDocId = templateSet.docs[0].id;
                    } else {
                        setError('No documents found in template set'); setLoading(false); return;
                    }
                }
                if (!targetDocId) { setError('No document ID provided'); setLoading(false); return; }

                setCurrentDocId(targetDocId);
                const res = await templateSetService.getTemplateDocById(targetDocId);
                const foundDoc = res.data;
                if (!foundDoc) { setError('Document not found (API returned empty data)'); setLoading(false); return; }

                setDoc(foundDoc);

                const md = foundDoc.metadata || {};
                const seeded = {
                    size: md.pageSize || 'A4',
                    marginTop:    typeof md.marginTop    === 'number' ? md.marginTop    : 96,
                    marginBottom: typeof md.marginBottom === 'number' ? md.marginBottom : 96,
                    marginLeft:   typeof md.marginLeft   === 'number' ? md.marginLeft   : 72,
                    marginRight:  typeof md.marginRight  === 'number' ? md.marginRight  : 72,
                };
                setPageSettings(seeded); pageSettingsRef.current = seeded;

                const sh = md.showHeader || false; const sf = md.showFooter || false;
                const hc = md.headerContent || ''; const fc = md.footerContent || '';
                setShowHeader(sh); showHeaderRef.current = sh;
                setShowFooter(sf); showFooterRef.current = sf;
                setHeaderContent(hc); headerContentRef.current = hc;
                setFooterContent(fc); footerContentRef.current = fc;
                const st = md.stamp === true; setStamp(st); stampRef.current = st;

                if (foundDoc.contentKey) {
                    const r2Url = foundDoc.contentKey.startsWith('http')
                        ? foundDoc.contentKey
                        : `${R2_URL}/${foundDoc.contentKey}`;

                    let r2Content = null;
                    for (let attempt = 0; attempt < 2; attempt++) {
                        try {
                            const controller = new AbortController();
                            const timeoutId = setTimeout(() => controller.abort(), 12000);
                            const r2Res = await fetch(`${r2Url}?t=${Date.now()}`, { signal: controller.signal });
                            clearTimeout(timeoutId);
                            if (r2Res.ok) {
                                const text = await r2Res.text();
                                try {
                                    let parsed = JSON.parse(text);
                                    if (typeof parsed === 'string') { console.warn('[DocumentEditorPage] R2 double-stringified — unwrapping'); parsed = JSON.parse(parsed); }
                                    r2Content = JSON.stringify(parsed); break;
                                } catch { console.warn('R2 not valid JSON:', text.slice(0, 120)); break; }
                            } else {
                                console.warn(`R2 attempt ${attempt + 1} failed: HTTP ${r2Res.status}`);
                                if (attempt === 0) await new Promise(res => setTimeout(res, 1500));
                            }
                        } catch (fetchErr) {
                            console.warn(`R2 attempt ${attempt + 1} error:`, fetchErr?.message);
                            if (attempt === 0) await new Promise(res => setTimeout(res, 1500));
                        }
                    }

                    if (r2Content) {
                        setInitialState(r2Content); lexicalJsonRef.current = r2Content; return;
                    } else {
                        console.warn('[DocumentEditorPage] R2 load failed after retries for:', foundDoc.contentKey);
                    }
                }

                const legacy = foundDoc.content || foundDoc.body;
                if (legacy) {
                    try {
                        const parsed = typeof legacy === 'string' ? JSON.parse(legacy) : legacy;
                        if (parsed?.root) {
                            const legacyStr = typeof legacy === 'string' ? legacy : JSON.stringify(legacy);
                            setInitialState(legacyStr); lexicalJsonRef.current = legacyStr; return;
                        }
                    } catch { /* not JSON — open empty */ }
                }
                if (foundDoc.contentKey) setContentLoadFailed(true);
            } catch (err) {
                setError(err.message || 'Failed to load document');
            } finally { setLoading(false); }
        };

        if (docId || (id && mode)) loadDoc();
    }, [docId, id, mode, reloadKey]);

    const handleChange = useCallback((json) => {
        lexicalJsonRef.current = json;
        if (!initialChangeConsumedRef.current) { initialChangeConsumedRef.current = true; return; }
        if (!contentDirtyRef.current) { contentDirtyRef.current = true; setContentDirty(true); }
    }, []);

    const handlePageSettingsChange = useCallback((next) => {
        pageSettingsRef.current = next; setPageSettings(next);
        if (!contentDirtyRef.current) { contentDirtyRef.current = true; setContentDirty(true); }
    }, []);

    const markContentDirty = () => {
        if (!contentDirtyRef.current) { contentDirtyRef.current = true; setContentDirty(true); }
    };
    const handleToggleHeader = useCallback(() => { setShowHeader(v => { showHeaderRef.current = !v; markContentDirty(); return !v; }); }, []);
    const handleToggleFooter = useCallback(() => { setShowFooter(v => { showFooterRef.current = !v; markContentDirty(); return !v; }); }, []);
    const handleHeaderChange = useCallback((text) => { setHeaderContent(text); headerContentRef.current = text; markContentDirty(); }, []);
    const handleFooterChange = useCallback((text) => { setFooterContent(text); footerContentRef.current = text; markContentDirty(); }, []);
    const handlePlaceholderDirtyChange = useCallback((d) => { placeholderDirtyRef.current = d; setPlaceholderDirty(d); }, []);

    const doSave = useCallback(async ({ silent = true } = {}) => {
        if (!contentDirtyRef.current && !placeholderDirtyRef.current) return { ok: true, skipped: true };
        setSaving(true);
        try {
            if (contentDirtyRef.current && currentDocId) {
                const ps = pageSettingsRef.current || null;
                const metadata = {
                    ...(ps ? { pageSize: ps.size, marginTop: ps.marginTop, marginBottom: ps.marginBottom, marginLeft: ps.marginLeft, marginRight: ps.marginRight } : {}),
                    showHeader: showHeaderRef.current, showFooter: showFooterRef.current,
                    headerContent: headerContentRef.current, footerContent: footerContentRef.current,
                    stamp: stampRef.current,
                };
                if (isLexicalContentEmpty(lexicalJsonRef.current)) {
                    console.warn('doSave: skipping R2 write — Lexical content blank');
                    contentDirtyRef.current = false; setContentDirty(false);
                } else {
                    await templateSetService.updateTemplateDocContent(currentDocId, { lexicalJson: lexicalJsonRef.current, metadata });
                    contentDirtyRef.current = false; setContentDirty(false);
                }
            }
            if (placeholderDirtyRef.current) {
                const phResult = await placeholderManagerRef.current?.saveAll?.();
                if (phResult && !phResult.ok) {
                    notifications.show({ title: 'Some placeholders failed to save', message: phResult.failed.map(f => `${f.slug}: ${f.message}`).join('; '), color: 'red' });
                    return { ok: false };
                }
                placeholderDirtyRef.current = false; setPlaceholderDirty(false);
            }
            setLastSavedAt(Date.now());
            placeholderManagerRef.current?.reload?.();
            if (!silent) notifications.show({ title: 'Saved', color: 'green', icon: <IconCheck size={16} />, autoClose: 2000 });
            return { ok: true };
        } catch (err) {
            notifications.show({ title: 'Save failed', message: err.message, color: 'red' });
            return { ok: false };
        } finally { setSaving(false); }
    }, [currentDocId]);

    useEffect(() => {
        const t = setInterval(() => setNowTick(x => x + 1), 5000);
        return () => clearInterval(t);
    }, []);

    useEffect(() => {
        const beforeUnload = (e) => {
            if (contentDirtyRef.current || placeholderDirtyRef.current) { e.preventDefault(); e.returnValue = ''; }
        };
        window.addEventListener('beforeunload', beforeUnload);
        return () => window.removeEventListener('beforeunload', beforeUnload);
    }, []);

    useEffect(() => () => {
        if (contentDirtyRef.current || placeholderDirtyRef.current) doSave({ silent: true });
    }, [doSave]);

    const navigateAfterSave = async (to) => {
        if (contentDirtyRef.current || placeholderDirtyRef.current) await doSave({ silent: true });
        navigate(to);
    };

    const backTarget = '/admin/template-sets';
    const handleSave  = () => doSave({ silent: true });
    const handleClose = () => navigateAfterSave(backTarget);
    const handleBack  = () => navigateAfterSave(backTarget);

    const handleOpenPreviewTab = async () => {
        const tsId = doc?.templateSet?.id || templateSetId || id;
        if (!tsId || !currentDocId) return;
        setPreviewLoading(true);
        try {
            if (contentDirtyRef.current || placeholderDirtyRef.current) {
                const res = await doSave({ silent: true });
                if (res && res.ok === false) return;
            }
            window.open(`/admin/doc-print-preview/${tsId}/${currentDocId}`, '_blank');
        } catch (err) {
            notifications.show({ title: 'Preview failed', message: err.message, color: 'red' });
        } finally { setPreviewLoading(false); }
    };

    /* ── Loading / error states ─────────────────────────────────────────────── */
    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-screen gap-2">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
                <span className="text-sm text-gray-500">Loading document...</span>
            </div>
        );
    }
    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-screen gap-3">
                <p className="text-red-500">{error}</p>
                <button type="button" onClick={handleBack} className="flex items-center gap-1 text-sm text-gray-600 hover:underline">
                    <IconArrowLeft size={14} /> Back
                </button>
            </div>
        );
    }

    const statusLabel = saving ? 'Saving…' : isDirty ? 'Unsaved changes' : lastSavedAt ? `Saved · ${formatSavedAgo(lastSavedAt)}` : '';
    const statusBadgeCls = saving
        ? 'bg-blue-100 text-blue-700'
        : isDirty
            ? 'bg-orange-100 text-orange-700'
            : 'bg-green-100 text-green-700';

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden', position: 'relative', zIndex: 150 }}>
            {/* ── Toolbar ─────────────────────────────────────────────────────── */}
            <div
                className="flex items-center justify-between px-4 py-1.5 bg-white flex-shrink-0"
                style={{ borderBottom: '2px solid #FF9900', boxShadow: '0 2px 6px rgba(255,153,0,0.1)' }}
            >
                <div className="flex items-center gap-2">
                    <img src="/images/logo.svg" alt="Sohozkaj" style={{ height: 22, width: 'auto', display: 'block', marginRight: 4 }} />
                    {doc?.templateSet?.name && (
                        <>
                            <span className="text-xs text-gray-500">{doc.templateSet.name}</span>
                            <span className="text-xs text-gray-400">/</span>
                        </>
                    )}
                    <span className="text-sm font-semibold">{doc?.title}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    {statusLabel && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadgeCls}`}>{statusLabel}</span>
                    )}

                    <IconTBtn
                        title={placeholderPanelOpen ? 'Hide placeholders' : 'Show placeholders'}
                        onClick={() => setPlaceholderPanelOpen(v => !v)}
                    >
                        {placeholderPanelOpen ? <IconLayoutSidebarRightCollapse size={18} /> : <IconLayoutSidebarRightExpand size={18} />}
                    </IconTBtn>

                    <TBtn
                        loading={previewLoading}
                        onClick={handleOpenPreviewTab}
                        className="bg-blue-50 text-blue-700 hover:bg-blue-100"
                    >
                        <IconEye size={14} /> Print Preview
                    </TBtn>

                    <IconTBtn
                        title={stamp ? 'Stamp document: ON' : 'Stamp document: OFF'}
                        active={stamp}
                        activeClass="bg-orange-500 text-white"
                        inactiveClass="text-gray-500 hover:bg-gray-100"
                        onClick={() => {
                            const next = !stamp;
                            setStamp(next); stampRef.current = next;
                            contentDirtyRef.current = true; setContentDirty(true);
                        }}
                    >
                        <IconRubberStamp size={18} />
                    </IconTBtn>

                    {contentLoadFailed && (
                        <TBtn
                            onClick={() => setReloadKey(k => k + 1)}
                            className="bg-orange-50 text-orange-700 hover:bg-orange-100"
                            title="Content failed to load from storage — click to retry"
                        >
                            <IconRefresh size={14} /> Reload content
                        </TBtn>
                    )}

                    <TBtn
                        loading={saving}
                        disabled={!isDirty && !saving}
                        onClick={handleSave}
                        className="bg-blue-600 text-white hover:bg-blue-700 font-medium"
                    >
                        <IconDeviceFloppy size={14} /> Save
                    </TBtn>

                    <TBtn onClick={handleClose} className="bg-red-50 text-red-600 hover:bg-red-100">
                        <IconX size={14} /> Close
                    </TBtn>
                </div>
            </div>

            {/* ── Editor + sidebar ─────────────────────────────────────────────── */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', overflow: 'hidden' }}>
                {/* Lexical editor */}
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <LexicalDocEditor
                        key={currentDocId}
                        initialState={initialState}
                        onChange={handleChange}
                        initialPageSettings={pageSettings}
                        onPageSettingsChange={handlePageSettingsChange}
                        showHeader={showHeader}
                        showFooter={showFooter}
                        onToggleHeader={handleToggleHeader}
                        onToggleFooter={handleToggleFooter}
                        headerContent={headerContent}
                        footerContent={footerContent}
                        onHeaderChange={handleHeaderChange}
                        onFooterChange={handleFooterChange}
                    />
                </div>

                {/* Placeholder sidebar */}
                {placeholderPanelOpen && (
                    <div style={{
                        width: 420, flexShrink: 0,
                        borderLeft: '2px solid #FF9900',
                        background: 'var(--background, #fff)',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        boxShadow: '-3px 0 12px rgba(255,153,0,0.08)',
                    }}>
                        {/* Sidebar header */}
                        <div style={{
                            background: 'linear-gradient(135deg, #c97700 0%, #FF9900 100%)',
                            padding: '10px 14px', display: 'flex', alignItems: 'center',
                            justifyContent: 'space-between', flexShrink: 0, borderBottom: '1px solid #b36500',
                        }}>
                            <div className="flex items-center gap-2">
                                <IconBraces size={15} color="rgba(255,255,255,0.9)" />
                                <span style={{ fontWeight: 700, fontSize: 14, color: 'white', letterSpacing: '0.02em' }}>
                                    Fields &amp; Steps
                                </span>
                            </div>
                            <div className="flex items-center gap-1">
                                <button
                                    type="button"
                                    title="Refresh placeholder list"
                                    onClick={() => placeholderManagerRef.current?.reload?.()}
                                    className="p-1 rounded transition-colors"
                                    style={{ color: 'rgba(255,255,255,0.8)' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <IconRefresh size={14} />
                                </button>
                                <button
                                    type="button"
                                    title="Hide sidebar"
                                    onClick={() => setPlaceholderPanelOpen(false)}
                                    className="p-1 rounded transition-colors"
                                    style={{ color: 'rgba(255,255,255,0.8)' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.15)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                >
                                    <IconX size={14} />
                                </button>
                            </div>
                        </div>

                        {/* Document switcher */}
                        {doc?.templateSet?.docs?.length > 1 && (
                            <div style={{ borderBottom: '1px solid #ffe0a0', background: '#fffaf2', flexShrink: 0 }}>
                                <div style={{ padding: '6px 14px 4px', fontSize: 10, fontWeight: 700, color: '#b36500', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                                    Documents in this set
                                </div>
                                {doc.templateSet.docs.map((d) => {
                                    const isActive = String(d.id) === String(currentDocId);
                                    return (
                                        <div
                                            key={d.id}
                                            onClick={() => { if (!isActive) navigateAfterSave(`/admin/lexical-editor/${doc.templateSet.id}/${d.id}`); }}
                                            style={{
                                                padding: '6px 14px', fontSize: 12,
                                                fontWeight: isActive ? 700 : 400,
                                                color: isActive ? '#c97700' : '#6b7280',
                                                background: isActive ? '#fff3d0' : 'transparent',
                                                borderLeft: isActive ? '3px solid #FF9900' : '3px solid transparent',
                                                cursor: isActive ? 'default' : 'pointer',
                                                transition: 'background 0.12s', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}
                                            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#fff8e1'; }}
                                            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            {d.title || `Document ${d.id}`}
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        {/* Hint */}
                        <div style={{ padding: '6px 14px', background: '#f9fafb', borderBottom: '1px solid #ffe0a0', fontSize: 11, color: '#9a5800', lineHeight: 1.4 }}>
                            Placeholders used in the document. Define type, label, and options here.
                        </div>

                        <ScrollArea style={{ flex: 1 }} className="p-4">
                            <PlaceholderManager
                                ref={placeholderManagerRef}
                                templateSetId={doc?.templateSet?.id || templateSetId}
                                onDirtyChange={handlePlaceholderDirtyChange}
                            />
                        </ScrollArea>
                    </div>
                )}
            </div>
        </div>
    );
}
